import React from 'react'
import Script from 'next/script'
import { resolveErrorReportingConfig } from './config'
import { buildSentryOnLoadSnippet } from './sentry-on-load-snippet'

export const SENTRY_ON_LOAD_SCRIPT_ID = 'sentry-on-load'
export const SENTRY_LOADER_SCRIPT_ID = 'sentry-loader'

/**
 * The Sentry Loader Script, mounted once in the root layout.
 *
 * Deliberately NOT part of `<AnalyticsScripts>`: that component returns `null`
 * on capability URLs, and errors on those pages still have to be reported — the
 * requirement there is to SCRUB the URL, not to stop watching. `beforeSend`
 * does the scrubbing (see `sentry-on-load-snippet.ts`).
 *
 * The Loader is a ~1 KB stub. The SDK itself is not downloaded until an
 * unhandled error, an unhandled rejection, or a `Sentry.captureException` call
 * needs it — or until `preloadErrorReporting()` asks for it on the pages where
 * people actually do things. That dormancy is why the marketing pages can carry
 * error reporting at all without paying ~30 KB and a main-thread init on every
 * visit, the overwhelming majority of which never hit an error. The npm
 * `@sentry/nextjs` package has no equivalent: it lands in the first-load bundle.
 *
 * The two tags are ordered, and the order is the contract: `window.sentryOnLoad`
 * must exist before the Loader runs, or `Sentry.init` never happens and the SDK
 * reports with no scrubbing configured. The definition is therefore a PLAIN
 * inline `<script>` in the server-rendered HTML — two `afterInteractive`
 * `next/script` tags have no guaranteed order between them.
 */
export function SentryLoaderScript() {
  const config = resolveErrorReportingConfig()
  if (!config) return null

  return (
    <>
      <script
        id={SENTRY_ON_LOAD_SCRIPT_ID}
        dangerouslySetInnerHTML={{ __html: buildSentryOnLoadSnippet(config) }}
      />
      <Script
        id={SENTRY_LOADER_SCRIPT_ID}
        src={config.loaderUrl}
        strategy="afterInteractive"
        crossOrigin="anonymous"
      />
    </>
  )
}
